import React, { useState } from "react";
import { FieldProps } from "formik";
import ErrorIcon from "@/assets/exclamation.svg";
import { Eye, EyeOff } from "lucide-react";
import { cn } from "@/lib/utils";

export const Input: React.FC<
  React.DetailedHTMLProps<
    React.InputHTMLAttributes<HTMLInputElement>,
    HTMLInputElement
  >
> = (props) => {
  const preventDefault = (e: WheelEvent) => {
    e.preventDefault();
  };
  return (
    <input
      {...props}
      className={cn(
        "file:text-foreground placeholder:text-muted-foreground dark:bg-input/30 flex h-9 w-full min-w-0 rounded-md bg-transparent px-3 py-1 text-base shadow-xs transition-[color,box-shadow] outline-none file:inline-flex file:h-7 file:border-0 file:bg-transparent file:text-sm file:font-medium disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50 md:text-sm",
        "border-input aria-invalid:border-destructive aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 bg-accent border",
        "selection:bg-secondary-foreground selection:text-background",
        props.className,
      )}
      onFocus={(e) => {
        e.target.addEventListener("wheel", preventDefault, {
          passive: false,
        });
      }}
    />
  );
};

interface InputFieldBaseProps {
  className?: string;
  inputClassName?: string;
  errorClassName?: string;
  labelClassName?: string;
  label?: string;
  prefix?: string; // to be added before the input, like a currency
  prefixClassName?: string;
  suffix?: string;
  suffixClassName?: string;
  iconClassName?: string;
  placeholder?: string;
  alignStart?: boolean;
  innerClassName?: string;
  type?: React.HTMLInputTypeAttribute;
  ignoreZeroValue?: boolean;
}

export type InputFieldProps = InputFieldBaseProps & FieldProps;

export const InputField: React.FC<InputFieldProps> = ({
  field,
  label,
  prefix,
  prefixClassName,
  suffix,
  suffixClassName,
  form: { touched, errors },
  className,
  inputClassName,
  labelClassName,
  errorClassName,
  iconClassName,
  innerClassName,
  type = "text",
  ignoreZeroValue,
  ...props
}) => {
  const [showPassword, setShowPassword] = useState(false);
  const inputType =
    type === "password" ? (showPassword ? "text" : "password") : type;

  return (
    <div className={cn("w-full", className)}>
      <div
        className={cn(
          label && "flex flex-col gap-[6px]",
          "relative",
          innerClassName,
        )}
      >
        {label && (
          <label
            htmlFor={field.name}
            className={cn("text-sm", labelClassName)}
            id={field.name}
          >
            {label}
          </label>
        )}
        {prefix && <span className={prefixClassName}>{prefix}</span>}
        <div className="relative">
          <Input
            type={inputType}
            {...field}
            {...props}
            value={ignoreZeroValue ? field.value || "" : field.value}
            className={cn(
              inputClassName,
              !!prefix && "pl-6",
              touched[field.name] &&
                errors[field.name] &&
                "border !border-[#F04438]",
              type === "number" &&
                "appearance-none [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none",
            )}
          />
          {type === "password" && (
            <button
              type="button"
              onClick={() => setShowPassword((prev) => !prev)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground focus:outline-none"
            >
              {showPassword ? (
                <EyeOff className="w-5 h-5" />
              ) : (
                <Eye className="w-5 h-5" />
              )}
            </button>
          )}
          {suffix && (
            <span className={cn(suffixClassName, "absolute top-[50%]")}>
              {suffix}
            </span>
          )}
          {touched[field.name] && errors[field.name] && (
            <img
              src={ErrorIcon}
              alt=""
              className={cn(
                "absolute top-1/2 right-[12px] z-5 -translate-y-1/2 text-[14px] font-light",
                iconClassName,
              )}
            />
          )}
        </div>
      </div>
      {touched[field.name] && errors[field.name] && (
        <div className={cn("mt-1 ml-1 text-sm text-[#F04438]", errorClassName)}>
          {errors[field.name]?.toString()}
        </div>
      )}
    </div>
  );
};
